import { DataItem } from 'scrivito'

import { FilterValues, WeekRange } from '../types'
import { weekRangeToApiFilters } from './dateUtils'
import { ensureString } from '@/utils/ensureString'

export const extractUniqueFieldValues = (
  items: DataItem[],
  fieldName: string,
): string[] =>
  Array.from(
    new Set(
      items.map((item) => ensureString(item.get(fieldName))).filter(Boolean),
    ),
  )

export const createItemNameMap = (
  items: DataItem[],
  nameFields: string[],
): Map<string, string> => {
  const map = new Map<string, string>()
  items.forEach((item) => {
    const name =
      nameFields.map((field) => ensureString(item.get(field))).find(Boolean) ||
      item.id()
    map.set(item.id(), name)
  })
  return map
}

type ApiFilterValue = string | boolean

const FIELD_FILTER_MAP: Record<string, string> = {
  customer: 'client',
  order: 'order',
  workPackage: 'position',
  status: 'status',
}

type BuildQueryFiltersParams = {
  userId: string
  appliedFilters?: FilterValues
  weekRange?: WeekRange
  userScope?: 'user' | 'accountDirector'
  showAll?: boolean
}

const userScopeFilter = ({
  userId,
  appliedFilters,
  userScope = 'user',
  showAll,
}: Omit<BuildQueryFiltersParams, 'weekRange'>): Record<
  string,
  ApiFilterValue
> => {
  if (showAll) return {}
  if (appliedFilters?.team === 'all') return {}
  if (appliedFilters?.team && !appliedFilters.isTeamLeader) {
    return { user: appliedFilters.team }
  }
  const id = appliedFilters?.team || userId
  return { [userScope]: id }
}

const fieldFilters = (
  appliedFilters?: FilterValues,
): Record<string, string> => {
  if (!appliedFilters) return {}
  return Object.entries(FIELD_FILTER_MAP).reduce<Record<string, string>>(
    (acc, [src, dst]) => {
      const value = appliedFilters[src as keyof FilterValues]
      if (typeof value === 'string' && value) acc[dst] = value
      return acc
    },
    {},
  )
}

const billableFilter = (
  appliedFilters?: FilterValues,
): Record<string, boolean> =>
  typeof appliedFilters?.billable === 'boolean'
    ? { billable: appliedFilters.billable }
    : {}

export const buildQueryFilters = ({
  userId,
  appliedFilters,
  weekRange,
  userScope = 'user',
  showAll = false,
}: BuildQueryFiltersParams): Record<string, ApiFilterValue> => ({
  ...userScopeFilter({ userId, appliedFilters, userScope, showAll }),
  ...(weekRange ? weekRangeToApiFilters(weekRange) : {}),
  ...fieldFilters(appliedFilters),
  ...billableFilter(appliedFilters),
})
